import React, { useState, useMemo, useCallback } from 'react';
import { Header } from '../layout/Header';
import { ProfileHeader, type ProfileData } from './ProfileHeader';
import { MediaTab } from './MediaTab';
import { GigsTab } from './GigsTab';
import { ServicesTab } from './ServicesTab';
import { EventsTab } from './EventsTab';
import { InventoryTab } from './InventoryTab';
// import { FinishSetupBadge } from '../common/FinishSetupBadge';
import { useGetPublicProfile, useGetUserProfile } from '../../api/profile';
import { useGetMyMedia } from '../../api/media';
import { useGetGigs } from '../../api/queries';
import { useGetMyServices } from '../../api/services';
import { useGetProfileEvents, type EventItem } from '../../api/events';
import { useAuth } from '../../hooks/useAuth';
import { useEffect } from 'react';
import { FiArrowLeft, FiUser } from 'react-icons/fi';
import { ProfileStatusState } from './ProfileStatusState';
import { useNavigate } from '@tanstack/react-router';
import type { MediaItem } from '../../api/media';
import type { ServiceItem } from '../../api/services';

type TabId = 'media' | 'gigs' | 'services' | 'events' | 'inventory';

interface ProfilePageProps {
  /** When undefined, shows the logged-in user's own profile */
  identifier?: string | number;
}

// ─── Normalise raw API profile → ProfileData ──────────────────────────────────

function normaliseProfile(raw: any): ProfileData {
  return {
    id: raw?.id ?? raw?.user_id ?? '',
    full_name: raw?.full_name ?? raw?.fullName,
    username: raw?.username,
    bio: raw?.bio,
    // profile_image is the public API field; prof_pics used elsewhere
    prof_pics:
      raw?.profile_image ??
      raw?.prof_pics ??
      raw?.prof_pics_url ??
      raw?.profPics ??
      raw?.google_prof_pics ??
      null,
    // cover_photo_url is the public API field
    cover_photo: raw?.cover_photo_url ?? raw?.cover_photo ?? raw?.coverPhoto ?? null,
    follower_count: raw?.follower_count ?? raw?.followers_count ?? raw?.followerCount,
    following_count: raw?.following_count ?? raw?.followingCount,
    views: raw?.views ?? raw?.view_count,
    is_verified: raw?.is_verified ?? raw?.verified ?? false,
    spotify: raw?.visible_social_links?.spotify ?? raw?.social_links?.spotify ?? raw?.spotify,
    apple_music:
      raw?.visible_social_links?.apple_music ??
      raw?.social_links?.apple_music ??
      raw?.apple_music ??
      raw?.appleMusic,
    soundcloud:
      raw?.visible_social_links?.soundcloud ?? raw?.social_links?.soundcloud ?? raw?.soundcloud,
    instagram:
      raw?.visible_social_links?.instagram ?? raw?.social_links?.instagram ?? raw?.instagram,
    twitter: raw?.visible_social_links?.twitter ?? raw?.social_links?.twitter ?? raw?.twitter,
    tiktok: raw?.visible_social_links?.tiktok ?? raw?.social_links?.tiktok ?? raw?.tiktok,
    website: raw?.visible_social_links?.website ?? raw?.social_links?.website ?? raw?.website,
    is_following: raw?.is_following ?? raw?.isFollowing ?? false,
    account_status: raw?.account_status ?? raw?.status ?? 'active',
    subscriptions_enabled: raw?.subscriptions_enabled ?? true,
    email_features_eligible: raw?.email_features_eligible ?? raw?.email_features_enabled ?? true,
    subscriber_count: raw?.subscriber_count ?? 0,
    is_subscribed: raw?.is_subscribed ?? false,
  };
}

// ─── Tab button ───────────────────────────────────────────────────────────────

function TabButton({
  id,
  label,
  active,
  onClick,
}: {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      id={`profile-tab-${id}`}
      onClick={onClick}
      className={`relative flex-1 py-3 text-[14px] font-medium transition-colors ${
        active ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
      }`}
    >
      {label}
      {active && (
        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-t-full" />
      )}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const ProfilePage: React.FC<ProfilePageProps> = ({ identifier }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('media');

  const isOwn =
    !identifier || String(identifier) === String(user?.id) || identifier === user?.username;

  // ── Profile data ──────────────────────────────────────────────────────────
  // For own profile: use auth user data directly (already hydrated via AuthContext)
  // For other users: fetch from public API
  const {
    data: publicProfileRaw,
    isLoading: profileLoading,
    isError: publicProfileError,
  } = useGetPublicProfile(!isOwn ? identifier : undefined);

  // If authenticated and viewing someone else, also fetch their profile by numeric ID
  // to get follow status and other auth-only fields correctly.
  const resolvedId = publicProfileRaw?.id ?? publicProfileRaw?.user_id;
  const { data: authProfileRaw } = useGetUserProfile(
    !isOwn && isAuthenticated && resolvedId ? resolvedId : undefined,
  );

  const profile: ProfileData = isOwn
    ? normaliseProfile(user)
    : normaliseProfile({ ...publicProfileRaw, ...authProfileRaw });

  // ── Media ─────────────────────────────────────────────────────────────────
  // Own profile: fetch via authenticated API. Non-own: read from profile response.
  const { data: myMediaData, isLoading: myMediaLoading } = useGetMyMedia(1, { enabled: isOwn });
  const mediaItems: MediaItem[] = useMemo(() => {
    if (isOwn) return myMediaData?.results ?? [];
    const raw: any[] = publicProfileRaw?.media_items ?? publicProfileRaw?.media ?? [];
    return raw.map(
      (m) =>
        ({
          id: m.id,
          user: m.creator?.id ?? 0,
          user_id: m.creator?.id,
          full_name: m.creator?.full_name,
          username: m.creator?.username,
          title: m.caption ?? '',
          caption: m.caption,
          media_type: m.media_type as 'image' | 'video',
          file_url: Array.isArray(m.media_urls) ? (m.media_urls[0] ?? '') : '',
          media_urls: Array.isArray(m.media_urls) ? m.media_urls : [],
          video_poster_urls: m.video_poster_urls ?? null,
          collaborators: Array.isArray(m.collaborators) ? m.collaborators : [],
          creator: m.creator,
          event: m.event ?? undefined,
          is_following_creator: m.is_following_creator ?? false,
          created_at: m.created_at ?? '',
        }) as MediaItem,
    );
  }, [isOwn, myMediaData, publicProfileRaw]);
  const mediaLoading = isOwn ? myMediaLoading : profileLoading;

  // ── Services ──────────────────────────────────────────────────────────────
  // Own profile: fetch via authenticated API. Non-own: read from profile response.
  const { data: myServices = [], isLoading: myServicesLoading } = useGetMyServices(1, {
    enabled: isOwn,
  });
  const services: ServiceItem[] = useMemo(() => {
    if (isOwn) return myServices;
    const raw: any[] = publicProfileRaw?.services ?? [];
    return raw.map((s) => ({
      id: s.id,
      title: s.title ?? '',
      description: s.description,
      price: s.price,
      images: Array.isArray(s.images)
        ? s.images
        : typeof s.images === 'string' && s.images
          ? s.images
              .split(',')
              .map((u: string) => u.trim())
              .filter(Boolean)
          : [],
      work_example_links: Array.isArray(s.work_example_links)
        ? s.work_example_links
        : typeof s.work_example_links === 'string' && s.work_example_links
          ? [s.work_example_links]
          : [],
      user: 0,
      user_types: [],
      user_tags: [],
      created_at: s.created_at ?? '',
    }));
  }, [isOwn, myServices, publicProfileRaw]);
  const servicesLoading = isOwn ? myServicesLoading : profileLoading;

  // ── Events (paginated) ────────────────────────────────────────────────────
  // Own profile: paginated fetch via API. Non-own: read from profile response (no pagination needed).
  const PAGE_SIZE = 8;
  const [eventsPage, setEventsPage] = useState(1);
  const [allOwnEvents, setAllOwnEvents] = useState<EventItem[]>([]);

  // Reset accumulated list whenever the viewed profile changes
  useEffect(() => {
    setEventsPage(1);
    setAllOwnEvents([]);
  }, [profile.id]);

  const {
    data: eventsPageData,
    isLoading: ownEventsLoading,
    isFetching: ownEventsFetching,
  } = useGetProfileEvents(isOwn ? profile.id : undefined, eventsPage, PAGE_SIZE);

  // Accumulate pages into a single flat list (own profile only)
  useEffect(() => {
    if (!eventsPageData?.results) return;
    setAllOwnEvents((prev) =>
      eventsPage === 1 ? eventsPageData.results : [...prev, ...eventsPageData.results],
    );
  }, [eventsPageData, eventsPage]);

  const hasMoreEvents = isOwn && Boolean(eventsPageData?.next);
  const eventsLoading = isOwn ? ownEventsLoading : profileLoading;
  const eventsFetching = isOwn ? ownEventsFetching : false;

  const handleLoadMoreEvents = useCallback(() => {
    if (!ownEventsFetching) setEventsPage((p) => p + 1);
  }, [ownEventsFetching]);

  // Build final events list for the EventsTab
  const profileEvents: EventItem[] = useMemo(() => {
    if (!isOwn) {
      // Use events embedded directly in the profile response — no extra API call needed
      const raw: any[] = publicProfileRaw?.upcoming_events ?? publicProfileRaw?.events ?? [];
      return raw.map(
        (e) =>
          ({
            id: e.id ?? 0,
            event_uuid: e.event_uuid,
            event_title: e.event_title,
            info: e.info,
            start: e.start,
            end: e.end,
            location: e.location,
            venue: e.venue,
            address: e.address,
            city: e.city,
            state: e.state,
            country: e.country,
            timezone: e.timezone,
            event_type: e.event_type,
            is_cancelled: e.is_cancelled ?? false,
            genres: Array.isArray(e.genres) ? e.genres : [],
            featured_image: e.featured_image ?? null,
            medias: [],
            tickets: Array.isArray(e.tickets) ? e.tickets : [],
            collaborators: [],
          }) as EventItem,
      );
    }
    return allOwnEvents;
  }, [isOwn, allOwnEvents, publicProfileRaw]);

  // ── Gigs (paginated, filtered by profile user) ────────────────────────────
  const GIGS_PAGE_SIZE = 12;
  const [gigsPage, setGigsPage] = useState(1);
  const [allGigs, setAllGigs] = useState<any[]>([]);

  // Reset accumulated list whenever the viewed profile changes
  useEffect(() => {
    setGigsPage(1);
    setAllGigs([]);
  }, [profile.id]);

  const gigUserId = isOwn ? user?.id : profile.id;
  const {
    data: gigsPageData,
    isLoading: gigsLoading,
    isFetching: gigsFetching,
  } = useGetGigs(gigUserId ? { user: gigUserId, page: gigsPage, page_size: GIGS_PAGE_SIZE } : {});

  // Accumulate pages into a single flat list
  useEffect(() => {
    if (!gigsPageData?.results) return;
    setAllGigs((prev) =>
      gigsPage === 1 ? gigsPageData.results : [...prev, ...gigsPageData.results],
    );
  }, [gigsPageData, gigsPage]);

  const hasMoreGigs = Boolean(gigsPageData?.next);

  const handleLoadMoreGigs = useCallback(() => {
    if (!gigsFetching) setGigsPage((p) => p + 1);
  }, [gigsFetching]);

  const events = allGigs;

  // ── Redirects ────────────────────────────────────────────────────────────
  // Redirect to canonical username URL if we were accessed via old username or numeric ID
  useEffect(() => {
    if (
      !profileLoading &&
      profile?.username &&
      identifier &&
      String(identifier) !== profile.username &&
      isNaN(Number(identifier))
    ) {
      console.log(
        `[ProfilePage] Redirecting from old username "${identifier}" to "${profile.username}"`,
      );
      navigate({
        to: '/profile/$identifier',
        params: { identifier: profile.username },
        replace: true,
      });
    }
  }, [profile?.username, identifier, profileLoading, navigate]);

  // Show setup badge only for own profile and only when not 100% complete
  if ((isOwn && !user) || (!isOwn && profileLoading)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-zinc-600 text-sm">Loading profile...</div>
      </div>
    );
  }

  if (!isOwn && (publicProfileError || !publicProfileRaw)) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <ProfileStatusState status="deleted" onBack={() => window.history.back()} />
        </div>
      </div>
    );
  }

  if (!isOwn && profile.account_status && profile.account_status !== 'active') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <ProfileStatusState
            status={profile.account_status as 'deactivated' | 'suspended' | 'deleted'}
            onBack={() => window.history.back()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-[var(--header-height)]">
        {/* Breadcrumb nav */}
        <div className="max-w-[1440px] mx-auto px-4 pt-2 md:pt-4 pb-2">
          <nav className="flex items-center gap-1.5 text-[12px] font-medium">
            {window.history.length > 1 && (
              <>
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
                >
                  <FiArrowLeft size={14} />
                  <span>Back</span>
                </button>
                <span className="text-zinc-700 select-none">/</span>
              </>
            )}
            <span className="flex items-center gap-1.5 text-zinc-200">
              <FiUser size={13} className="text-blue-500/80" />
              <span>{profile.username || 'Profile'}</span>
            </span>
          </nav>
        </div>

        {/* Profile header */}
        <ProfileHeader profile={profile} isOwner={isOwn} />

        {/* Tab bar */}
        <div className="sticky top-[var(--header-height)] z-30 bg-black border-b border-white/8">
          <div className="flex">
            <TabButton
              id="media"
              label="Media"
              active={activeTab === 'media'}
              onClick={() => setActiveTab('media')}
            />
            <TabButton
              id="gigs"
              label="Gigs"
              active={activeTab === 'gigs'}
              onClick={() => setActiveTab('gigs')}
            />
            <TabButton
              id="services"
              label="Services"
              active={activeTab === 'services'}
              onClick={() => setActiveTab('services')}
            />
            <TabButton
              id="events"
              label="Events"
              active={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
            />
            {isOwn && (
              <TabButton
                id="inventory"
                label="Inventory"
                active={activeTab === 'inventory'}
                onClick={() => setActiveTab('inventory')}
              />
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="min-h-100">
          {activeTab === 'media' && <MediaTab media={mediaItems} isLoading={mediaLoading} />}
          {activeTab === 'gigs' && (
            <GigsTab
              events={events}
              isLoading={gigsLoading && gigsPage === 1}
              isLoadingMore={gigsFetching && gigsPage > 1}
              hasMore={hasMoreGigs}
              onLoadMore={handleLoadMoreGigs}
            />
          )}
          {activeTab === 'services' && (
            <ServicesTab services={services} isLoading={servicesLoading} />
          )}
          {activeTab === 'events' && (
            <EventsTab
              events={profileEvents}
              isLoading={eventsLoading && eventsPage === 1}
              isLoadingMore={eventsFetching && eventsPage > 1}
              isOwnProfile={isOwn}
              hasMore={hasMoreEvents}
              onLoadMore={handleLoadMoreEvents}
            />
          )}
          {activeTab === 'inventory' && <InventoryTab isOwn={isOwn} />}
        </div>
      </main>
    </div>
  );
};
